import React, { useState } from 'react';
import axios from 'axios';
import Layout from '@/components/Layout';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { toast } from 'sonner';
import { FileSpreadsheet, Search, Download } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function AdminAttendanceSummary() {
  const [summary, setSummary] = useState([]);
  const [month, setMonth] = useState(new Date().toISOString().slice(0, 7));
  const [loading, setLoading] = useState(false);

  const fetchSummary = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/attendance/summary?month=${month}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSummary(response.data);
    } catch (error) {
      toast.error('Failed to load attendance summary');
    } finally {
      setLoading(false);
    }
  };

  const handleExport = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/attendance/summary/export?month=${month}`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `Attendance_Summary_${month}.xlsx`);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      
      toast.success('Excel file downloaded successfully');
    } catch (error) {
      toast.error('Failed to export data');
    }
  };

  return (
    <Layout>
      <div className="space-y-8" data-testid="admin-attendance-summary-page">
        <div>
          <h1 className="text-4xl font-bold tracking-tight">Attendance Summary</h1>
          <p className="text-muted-foreground mt-2">Monthly attendance and salary summary for all employees</p>
        </div>

        <Card data-testid="summary-filter-card">
          <CardHeader>
            <CardTitle className="flex items-center">
              <Search className="mr-2 h-5 w-5" />
              Select Month
            </CardTitle>
            <CardDescription>Choose a month to view attendance summary</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="flex gap-4 items-end">
              <div className="flex-1 space-y-2">
                <Label htmlFor="month">Month</Label>
                <Input
                  id="month"
                  type="month"
                  value={month}
                  onChange={(e) => setMonth(e.target.value)}
                  data-testid="input-month"
                />
              </div>
              <Button onClick={fetchSummary} disabled={loading} data-testid="fetch-summary-button">
                <FileSpreadsheet className="mr-2 h-4 w-4" />
                {loading ? 'Loading...' : 'Generate Summary'}
              </Button>
              {summary.length > 0 && (
                <Button onClick={handleExport} variant="outline" data-testid="export-button">
                  <Download className="mr-2 h-4 w-4" />
                  Export to Excel
                </Button>
              )}
            </div>
          </CardContent>
        </Card>

        {summary.length > 0 && (
          <Card data-testid="summary-table-card">
            <CardHeader>
              <CardTitle>Attendance Summary - {month}</CardTitle>
              <CardDescription>Complete attendance and salary breakdown</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="w-full overflow-auto rounded-lg border border-border">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Employee</TableHead>
                      <TableHead>Designation</TableHead>
                      <TableHead>Base Salary</TableHead>
                      <TableHead>Present</TableHead>
                      <TableHead>Absent</TableHead>
                      <TableHead>Late</TableHead>
                      <TableHead>Half Days</TableHead>
                      <TableHead>Weekly Off</TableHead>
                      <TableHead>Holidays</TableHead>
                      <TableHead>Leaves Used</TableHead>
                      <TableHead>Deductions</TableHead>
                      <TableHead>Final Salary</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {summary.map((emp) => (
                      <TableRow key={emp.employee_id} data-testid="summary-row">
                        <TableCell className="font-medium">
                          <div>
                            <p>{emp.employee_name}</p>
                            <p className="text-xs text-muted-foreground">{emp.employee_email}</p>
                          </div>
                        </TableCell>
                        <TableCell>{emp.designation || '-'}</TableCell>
                        <TableCell>₹{emp.base_salary.toFixed(2)}</TableCell>
                        <TableCell className="text-green-600 font-medium">{emp.days_present}</TableCell>
                        <TableCell className="text-red-600 font-medium">{emp.days_absent}</TableCell>
                        <TableCell className="text-yellow-600 font-medium">{emp.days_late}</TableCell>
                        <TableCell>{emp.half_days}</TableCell>
                        <TableCell className="text-blue-600">{emp.days_weekly_off}</TableCell>
                        <TableCell className="text-purple-600">{emp.days_holiday}</TableCell>
                        <TableCell>{emp.paid_leaves_used}</TableCell>
                        <TableCell className="text-red-600">₹{emp.deductions.toFixed(2)}</TableCell>
                        <TableCell className="font-bold text-green-700">₹{emp.final_salary.toFixed(2)}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            </CardContent>
          </Card>
        )}
      </div>
    </Layout>
  );
}
